import type { PaginatedList, Person } from '@local/common-lib';
import {
  VisibilityOff as HideFinishedIcon,
  Person as NewPersonIcon,
  Visibility as ShowFinishedIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { NavLink } from '../../components/nav-link/nav-link.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useHistoryState } from '../../hooks/use-history-state.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { roles } from '../../utils/roles.js';
import { ErrorPage } from '../error/index.js';

const DEFAULT_PAGINATION = { limit: 50, skip: 0 };

export const PersonListPage: FC = () => {
  useSetTitle('People');
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuthenticated();
  const [pagination, setPagination] = useHistoryState(
    'pagination',
    DEFAULT_PAGINATION,
  );
  const [hideFinished, setHideFinished] = useHistoryState('hideFinished', true);

  const [persons] = useAxios<PaginatedList<Person.View>>(
    '/api/v1/persons',
    useMemo(
      () => ({
        params: {
          ...pagination,
          filter: hideFinished ? JSON.stringify({ finished: null }) : undefined,
          sort: 'lastName',
        },
      }),
      [pagination, hideFinished],
    ),
  );

  const [summaries] = useAxios<{ items: { _id: string; allTime: 0 }[] }>(
    '/api/v1/patrols/persons/summary',
    useMemo(
      () => ({
        data: {
          personIds: persons.data?.items.map((person) => person._id) ?? [],
        },
        method: 'POST',
      }),
      [persons.data],
    ),
    useMemo(
      () => ({ condition: !!persons.data?.items.length }),
      [persons.data],
    ),
  );

  const summaryMap =
    useMemo(
      () =>
        summaries.data?.items?.reduce((acc, item) => {
          acc.set(item._id, item.allTime);
          return acc;
        }, new Map<string, number>()),
      [summaries],
    ) ?? new Map<string, number>();

  if (persons.loading) {
    return <MiddleSpinner />;
  }

  if (persons.error) {
    return <ErrorPage error={persons.error} />;
  }

  return (
    <CenterLayout>
      <Stack spacing={3}>
        <Card>
          <CardActions sx={{ justifyContent: 'space-between' }}>
            <ToggleButton
              aria-label="Toggle hide finished"
              onChange={() => {
                setPagination(DEFAULT_PAGINATION);
                setHideFinished(!hideFinished);
              }}
              selected={hideFinished}
              size="small"
              value="check"
            >
              {hideFinished ? <HideFinishedIcon /> : <ShowFinishedIcon />}
            </ToggleButton>
            {auth.userInfo.hasRole('admin') && (
              <Button
                color="primary"
                component={NavLink}
                startIcon={<NewPersonIcon />}
                to="/person/new"
                variant="outlined"
              >
                New Person
              </Button>
            )}
          </CardActions>
          {smallScreen ? (
            <NarrowPersonList persons={persons.data} />
          ) : (
            <WidePersonList persons={persons.data} summaryMap={summaryMap} />
          )}
        </Card>

        {persons.data.matches > pagination.limit ? (
          <CenterLayout>
            <Pagination
              count={Math.ceil(persons.data.matches / pagination.limit)}
              onChange={(_, page) => {
                setPagination({
                  limit: pagination.limit,
                  skip: pagination.limit * (page - 1),
                });
              }}
              page={Math.floor(pagination.skip / pagination.limit) + 1}
              showFirstButton
              showLastButton
              sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
            />
          </CenterLayout>
        ) : undefined}
      </Stack>
    </CenterLayout>
  );
};

const NarrowPersonList: FC<{ persons: PaginatedList<Person.View> }> = ({
  persons,
}) => {
  const navigate = useNavigate();

  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {persons.items.map((row) => (
          <TableRow
            key={row._id}
            onClick={() => navigate(`/person/${row._id}`)}
            sx={{
              '&:last-child td, &:last-child th': { border: 0 },
              cursor: 'pointer',
            }}
          >
            <TableCell>
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  justifyContent: 'space-between',
                }}
              >
                {row.firstName} {row.lastName.toUpperCase()}
                {row.finished && (
                  <Chip
                    label="Finished"
                    size="small"
                    sx={{ backgroundColor: 'red' }}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const WidePersonList: FC<{
  persons: PaginatedList<Person.View>;
  summaryMap: Map<string, number>;
}> = ({ persons, summaryMap }) => {
  const navigate = useNavigate();

  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell sx={{ textAlign: 'center' }}>Since</TableCell>
          <TableCell sx={{ textAlign: 'center' }}>Patrols</TableCell>
          <TableCell sx={{ textAlign: 'end' }}>Roles</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {persons.items.map((row) => (
          <TableRow
            key={row._id}
            onClick={() => navigate(`/person/${row._id}`)}
            sx={{
              '&:last-child td, &:last-child th': { border: 0 },
              cursor: 'pointer',
            }}
          >
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {row.firstName} {row.lastName.toUpperCase()}
            </TableCell>
            <TableCell sx={{ textAlign: 'center' }}>
              {DateTime.fromJSDate(row.started).toLocaleString({
                year: 'numeric',
              })}
            </TableCell>
            <TableCell sx={{ textAlign: 'center' }}>
              {summaryMap.get(row._id)}
            </TableCell>
            <TableCell sx={{ textAlign: 'end' }}>
              {row.finished ? (
                <Chip
                  label="Finished"
                  size="small"
                  sx={{ backgroundColor: 'red', mb: 1, ml: 1 }}
                />
              ) : (
                row.roles
                  .sort()
                  .map((role) => (
                    <Chip
                      key={role}
                      label={roles[role] || role}
                      size="small"
                      sx={{ mb: 1, ml: 1 }}
                    />
                  ))
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
